/** Styled components for SWOTView's mood view, vote meter and scenario-detail modal (split out of SWOTView.styles.js, #1166). */
import styled from '@emotion/styled';

// ============ MOOD VIEW ============

export const MoodContainer = styled.div`
  flex: 1;
  padding: 40px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const MoodOverallCard = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 32px 48px;
  display: flex;
  align-items: center;
  gap: 48px;
`;

export const MoodOverallScore = styled.div`
  text-align: center;
  min-width: 180px;
`;

export const MoodOverallEmoji = styled.div`
  font-size: 4rem;
  margin-bottom: 8px;
`;

export const MoodOverallValue = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${props => props.$color};
  line-height: 1;
`;

export const MoodOverallLabel = styled.div`
  color: #888;
  font-size: 0.85rem;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

export const MoodOverallDescription = styled.div`
  flex: 1;
  color: #aaa;
  font-size: 1.1rem;
  line-height: 1.6;
`;

export const MoodScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

export const MoodScenarioCard = styled.div`
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(255,255,255,0.3);
    transform: translateY(-2px);
    background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%);
  }
`;

export const MoodScenarioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const MoodScenarioName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

export const MoodScenarioEmoji = styled.div`
  font-size: 1.8rem;
`;

export const MoodScenarioScore = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const MoodScenarioValue = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$color};
`;

export const MoodScenarioPercent = styled.span`
  font-size: 0.9rem;
  color: #888;
`;

export const MoodScenarioBar = styled.div`
  width: 100%;
  height: 6px;
  background: linear-gradient(to right, #F44336, #FF9800, #FFC107, #8BC34A, #4CAF50);
  border-radius: 3px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: ${props => props.$position}%;
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    transform: translateX(-50%);
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }
`;

export const MoodScenarioMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #666;
`;

export const MoodQuadrantBreakdown = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const MoodQuadrantChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
  background: ${props => props.$color}20;
  color: ${props => props.$color};
`;

// ============ VOTE METER ============

export const VoteMeterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 160px;
  padding: 16px 24px;
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
`;

export const VoteMeterLabel = styled.div`
  color: #888;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 12px;
`;

export const VoteMeterBar = styled.div`
  width: 100%;
  height: 12px;
  background: #222;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
`;

export const VoteMeterFillDown = styled.div`
  position: absolute;
  right: 50%;
  top: 0;
  height: 100%;
  background: linear-gradient(270deg, #F44336, #FF5722);
  border-radius: 6px 0 0 6px;
  transition: width 0.3s ease;
`;

export const VoteMeterFillUp = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  border-radius: 0 6px 6px 0;
  transition: width 0.3s ease;
`;

export const VoteMeterCenter = styled.div`
  position: absolute;
  left: 50%;
  top: -2px;
  width: 2px;
  height: 16px;
  background: rgba(255, 255, 255, 0.5);
  transform: translateX(-50%);
  z-index: 2;
  border-radius: 0 6px 6px 0;
  transition: width 0.3s ease;
`;

export const VoteCounts = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

export const VoteCount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.$color};

  svg {
    width: 18px;
    height: 18px;
  }
`;

// ============ SCENARIO DETAIL MODAL ============

export const ScenarioModalContent = styled.div`
  background: #0a0a0a;
  border: 2px solid ${props => props.$color || '#2196F3'};
  border-radius: 12px;
  padding: 24px 32px 32px 32px;
  width: 90vw;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  color: #fff;
  position: relative;
`;

export const ScenarioModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-right: 40px;
`;

export const ScenarioModalEmoji = styled.div`
  font-size: 3rem;
`;

export const ScenarioModalInfo = styled.div`
  flex: 1;
`;

export const ScenarioModalTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
`;

export const ScenarioModalScore = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

export const ScenarioModalValue = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$color};
`;

export const ScenarioModalLabel = styled.span`
  font-size: 0.9rem;
  color: #888;
`;

export const ScenarioQuadrantSection = styled.div`
  margin-bottom: 20px;
`;

export const ScenarioQuadrantHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.$color}40;
`;

export const ScenarioQuadrantIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${props => props.$color}30;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ScenarioQuadrantTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.$color};
`;

export const ScenarioQuadrantCount = styled.div`
  margin-left: auto;
  font-size: 0.8rem;
  color: #888;
`;

export const ScenarioItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ScenarioItem = styled.div`
  background: ${props => props.$color}10;
  border: 1px solid ${props => props.$color}30;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$color}20;
    border-color: ${props => props.$color}50;
  }
`;

export const ScenarioItemTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #fff;
  margin-bottom: 4px;
`;

export const ScenarioItemMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 0.75rem;
  color: #888;
`;

export const ScenarioEmptyMessage = styled.div`
  color: #666;
  font-size: 0.85rem;
  font-style: italic;
  padding: 8px 0;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.$color};
`;

export const LegendLabel = styled.div`
  color: #ccc;
  font-size: 0.9rem;
`;

export const LegendValue = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 1.1rem;
  margin-left: auto;
`;
